// hooks/useGeolocation.js
// Custom hook wrapping navigator.geolocation.watchPosition() with high accuracy
// Designed for iPad field use - tracks GPS coordinates, altitude, and accuracy in real-time

import { useState, useEffect, useRef } from "react";

// Detect iOS Chrome (or any non-Safari iOS browser using WKWebView)
const isIOSChrome = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && !isSafari;
};

export const useGeolocation = (enabled = false) => {
  const [position, setPosition] = useState({
    latitude: null,
    longitude: null,
    altitude: null,
    accuracy: null,
    altitudeAccuracy: null,
    timestamp: null,
    error: null,
    errorCode: null,
  });
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      if (!navigator.geolocation) {
        setPosition((prev) => ({
          ...prev,
          error: "Geolocation not supported",
          errorCode: "NOT_SUPPORTED",
        }));
      }
      return;
    }

    const options = {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 15000,
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) =>
        setPosition({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          altitude: pos.coords.altitude,
          accuracy: pos.coords.accuracy,
          altitudeAccuracy: pos.coords.altitudeAccuracy,
          timestamp: pos.timestamp,
          error: null,
          errorCode: null,
        }),
      (err) => {
        // err.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        let errorCode = "UNKNOWN";
        if (err.code === 1) errorCode = "PERMISSION_DENIED";
        else if (err.code === 2) errorCode = "POSITION_UNAVAILABLE";
        else if (err.code === 3) errorCode = "TIMEOUT";

        setPosition((prev) => ({
          ...prev,
          error: err.message,
          errorCode,
          isIOSChrome: isIOSChrome(),
        }));
      },
      options,
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled]);

  return position;
};
